import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Link as MuiLink,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import SidePanel from '../components/ui/SidePanel';
import {
  People,
  Security,
  Receipt,
  CalendarMonth,
  Cloud,
  Settings,
  Rocket,
  Check,
  Menu as MenuIcon,
} from '@mui/icons-material';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <People sx={{ fontSize: 28, color: 'primary.main' }} />,
      title: 'Multi-Company Management',
      description: 'Manage multiple accounting firms from a single platform with complete data isolation.',
    },
    {
      icon: <Security sx={{ fontSize: 28, color: 'success.main' }} />,
      title: 'Secure & Compliant',
      description: 'Bank-level security with SOC 2 compliance and end-to-end encryption.',
    },
    {
      icon: <Receipt sx={{ fontSize: 28, color: 'secondary.main' }} />,
      title: 'Advanced Billing',
      description: 'Automated invoicing, payment processing, and financial reporting.',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 28, color: 'warning.main' }} />,
      title: 'Workflow Automation',
      description: 'Streamline tax preparation workflows with automated task management.',
    },
    {
      icon: <Cloud sx={{ fontSize: 28, color: 'info.main' }} />,
      title: 'Cloud-Based',
      description: 'Access your data anywhere, anytime with our cloud infrastructure.',
    },
    {
      icon: <Settings sx={{ fontSize: 28, color: 'error.main' }} />,
      title: 'Customizable',
      description: "Tailor the platform to your firm's specific needs and processes.",
    },
  ];

  const pricingPlans = [
    { id: 'starter', name: 'Starter', price: '$99', period: '/month', features: ['Up to 5 team members', 'Basic client management', 'Standard reporting', 'Email support'], popular: false },
    { id: 'professional', name: 'Professional', price: '$199', period: '/month', features: ['Up to 20 team members', 'Advanced client management', 'Custom workflows', 'Priority support', 'API access'], popular: true },
    { id: 'enterprise', name: 'Enterprise', price: 'Custom', period: '', features: ['Unlimited team members', 'Custom integrations', 'Dedicated support', 'On-premise options', 'Custom development'], popular: false },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Firmflow
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              <MuiLink href="#features" sx={{ color: 'text.secondary', textDecoration: 'none', fontWeight: 500, '&:hover': { color: 'text.primary' } }}>
                Features
              </MuiLink>
              <MuiLink href="#pricing" sx={{ color: 'text.secondary', textDecoration: 'none', fontWeight: 500, '&:hover': { color: 'text.primary' } }}>
                Pricing
              </MuiLink>
              <Button component={Link} to="/register" variant="contained" size="medium" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Register Company
              </Button>
              <Button component={Link} to="/login" color="primary" variant="outlined" size="medium">
                Login
              </Button>
            </Box>
            <IconButton sx={{ display: { md: 'none' } }} onClick={() => setMobileMenuOpen(true)} aria-label="Menu">
              <MenuIcon />
            </IconButton>
          </Box>
          <SidePanel
            id="mobile-nav-menu"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            title="Menu"
            width="sm"
            side="right"
            stackable={false}
          >
            <List>
              <ListItem disablePadding>
                <ListItemButton component="a" href="#features" onClick={() => setMobileMenuOpen(false)}>
                  <ListItemText primary="Features" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component="a" href="#pricing" onClick={() => setMobileMenuOpen(false)}>
                  <ListItemText primary="Pricing" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <ListItemText primary="Register Company" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
            </List>
          </SidePanel>
        </Container>
      </Paper>

      {/* Hero Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 640 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
              The Complete{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                CPA Practice Management Platform
              </Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Streamline your CPA firm with Firmflow — all-in-one practice management. Manage clients, automate
              workflows, and grow your business with enterprise-grade security and scalability.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                startIcon={<Rocket />}
                sx={{ px: 3, py: 1.5 }}
              >
                Start Free Trial
              </Button>
              <Button
                component={Link}
                to="/demo"
                variant="outlined"
                size="large"
                sx={{ px: 3, py: 1.5, borderColor: 'primary.main', color: 'primary.main', bgcolor: 'primary.50' }}
              >
                Request Demo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Features
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'text.primary' }}>
              Everything you need to run your accounting firm
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
              Our platform provides all the tools you need to manage clients, automate workflows, and grow your business.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 1, bgcolor: 'primary.50' }}>
                  {feature.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Simple, transparent pricing
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Choose the plan that's right for your firm
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, maxWidth: 1000, mx: 'auto' }}>
            {pricingPlans.map((plan, index) => (
              <Paper
                key={index}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: plan.popular ? 'primary.main' : 'divider',
                  ...(plan.popular && { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -1 }),
                }}
              >
                {plan.popular && (
                  <Typography variant="caption" sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 10, bgcolor: 'primary.50', color: 'primary.main', fontWeight: 600, mb: 2 }}>
                    Most popular
                  </Typography>
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{plan.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{plan.price}</Typography>
                  <Typography variant="body2" color="text.secondary">{plan.period}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Everything you need to get started
                </Typography>
                <Box component="ul" sx={{ mt: 3, pl: 2.5, listStyle: 'none' }}>
                  {plan.features.map((feature, featureIndex) => (
                    <Box key={featureIndex} component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Check sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  component={Link}
                  to={`/register?plan=${plan.id}`}
                  variant={plan.popular ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  Get started
                </Button>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', py: 8, px: 2 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
              Ready to transform your practice?
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.100', mb: 2 }}>
              Start your free trial today.
            </Typography>
            <Typography variant="body1" sx={{ color: 'primary.200', mb: 4 }}>
              Join thousands of accounting firms already using our platform.
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
            >
              Get started for free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.300', py: 6, px: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', gap: 4 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                Firmflow
              </Typography>
              <Typography variant="body2">
                The complete CPA practice management platform for modern accounting firms.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, mt: { xs: 4, md: 0 } }}>
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'grey.400', fontWeight: 600 }}>
                  Product
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 0, listStyle: 'none' }}>
                  <li><MuiLink href="#features" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Features</MuiLink></li>
                  <li><MuiLink href="#pricing" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Pricing</MuiLink></li>
                  <li><MuiLink href="#features" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Security</MuiLink></li>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'grey.400', fontWeight: 600 }}>
                  Support
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 0, listStyle: 'none' }}>
                  <li><MuiLink href="mailto:support@firmflow.site" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Help Center</MuiLink></li>
                  <li><MuiLink href="mailto:support@firmflow.site" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Contact</MuiLink></li>
                  <li><MuiLink href="/" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}>Status</MuiLink></li>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ borderTop: '1px solid', borderColor: 'grey.700', pt: 3, mt: 4, textAlign: { md: 'center' } }}>
            <Typography variant="body2" color="grey.400">
              © 2024 Firmflow. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
